import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, Calendar, LogOut, Plus, X, Check, 
  UserX, Clock, Upload, FileText, Eye, Bell,
  MessageSquare, Heart, Image as ImageIcon, Trash2, Star, Sparkles, Settings, CalendarCheck
} from 'lucide-react';
import OnlineUsers from '../components/OnlineUsers';
import ContentManager from '../components/ContentManager';
import { toast } from 'sonner';
import './AdminDashboard.css';
import { DAY_ORDER, DAY_LABELS } from '../lib/businessHours';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('employees');
  const [user, setUser] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [timeoffRequests, setTimeoffRequests] = useState([]);
  const [shiftSwapRequests, setShiftSwapRequests] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [posts, setPosts] = useState([]);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [businessHours, setBusinessHours] = useState(null);
  const [savingHours, setSavingHours] = useState(false);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showAddAnnouncement, setShowAddAnnouncement] = useState(false);
  const [showAddFeaturedEvent, setShowAddFeaturedEvent] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scheduleFile, setScheduleFile] = useState(null);
  const [weekStart, setWeekStart] = useState('');
  const [weekEnd, setWeekEnd] = useState('');
  const [postContent, setPostContent] = useState('');
  const [postImage, setPostImage] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentText, setCommentText] = useState({});
  
  const [newEmployee, setNewEmployee] = useState({
    employee_id: '',
    name: '',
    password: '',
    position: '',
    is_active: true,
    email: ''
  });

  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    message: ''
  });

  const [newFeaturedEvent, setNewFeaturedEvent] = useState({
    title: '',
    subtitle: '',
    date: '',
    time: '',
    description: '',
    image_url: ''
  });

  const navigate = useNavigate();

  const [fetchError, setFetchError] = useState(null);

  const loadEmployees = useCallback(async () => {
    try {
      setFetchError(null);
      const response = await axios.get(`${API}/admin/employees`, {
        withCredentials: true
      });
      setEmployees(response.data.employees);
    } catch (err) {
      console.error('Failed to load employees:', err.message);
      setFetchError('Unable to load employees');
    }
  }, []);

  const loadTimeoffRequests = useCallback(async () => {
    try {
      setFetchError(null);
      const response = await axios.get(`${API}/admin/timeoff-requests`, {
        withCredentials: true
      });
      setTimeoffRequests(response.data.requests);
    } catch (err) {
      console.error('Failed to load time-off requests:', err.message);
      setFetchError('Unable to load requests');
    }
  }, []);

  const loadReservations = useCallback(async () => {
    try {
      setFetchError(null);
      const response = await axios.get(`${API}/admin/reservations`, {
        withCredentials: true
      });
      setReservations(response.data.reservations);
    } catch (err) {
      console.error('Failed to load reservations:', err.message);
      setFetchError('Unable to load reservations');
    }
  }, []);

  const loadBusinessHours = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/business-hours`);
      setBusinessHours(response.data.hours);
    } catch (err) {
      console.error('Failed to load business hours:', err.message);
    }
  }, []);

  const updateDayHours = (dayKey, field, value) => {
    setBusinessHours((prev) => ({
      ...prev,
      [dayKey]: { ...prev[dayKey], [field]: value }
    }));
  };

  const handleSaveHours = async () => {
    if (savingHours || !businessHours) return;
    setSavingHours(true);
    try {
      await axios.put(`${API}/admin/business-hours`, businessHours, { withCredentials: true });
      toast.success('Business hours updated successfully.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update business hours.');
    } finally {
      setSavingHours(false);
    }
  };

  const loadShiftSwapRequests = useCallback(async () => {
    try {
      setFetchError(null);
      const response = await axios.get(`${API}/admin/shift-swap`, {
        withCredentials: true
      });
      setShiftSwapRequests(response.data.requests);
    } catch (err) {
      console.error('Failed to load shift swap requests:', err.message);
      setFetchError('Unable to load shift swaps');
    }
  }, []);

  const loadAnnouncements = useCallback(async () => {
    try {
      setFetchError(null);
      const response = await axios.get(`${API}/announcements`, {
        withCredentials: true
      });
      setAnnouncements(response.data.announcements);
    } catch (err) {
      console.error('Failed to load announcements:', err.message);
      setFetchError('Unable to load announcements');
    }
  }, []);

  const loadPosts = useCallback(async () => {
    try {
      setFetchError(null);
      const response = await axios.get(`${API}/feed/posts`, {
        withCredentials: true
      });
      setPosts(response.data.posts);
    } catch (err) {
      console.error('Failed to load posts:', err.message);
      setFetchError('Unable to load posts');
    }
  }, []);

  const loadFeaturedEvents = useCallback(async () => {
    try {
      setFetchError(null);
      const response = await axios.get(`${API}/featured-events`, {
        withCredentials: true
      });
      setFeaturedEvents(response.data);
    } catch (err) {
      console.error('Failed to load featured events:', err.message);
      setFetchError('Unable to load featured events');
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.role !== 'admin') {
        navigate('/team');
        return;
      }
      
      setUser(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Admin auth check failed:', err.message);
      navigate('/team');
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) {
      if (activeTab === 'employees') {
        loadEmployees();
      } else if (activeTab === 'reservations') {
        loadReservations();
      } else if (activeTab === 'timeoff') {
        loadTimeoffRequests();
      } else if (activeTab === 'shiftswap') {
        loadShiftSwapRequests();
      } else if (activeTab === 'announcements') {
        loadAnnouncements();
      } else if (activeTab === 'feed') {
        loadPosts();
      } else if (activeTab === 'featured') {
        loadFeaturedEvents();
      } else if (activeTab === 'hours') {
        loadBusinessHours();
      }
    }
  }, [activeTab, user, loadEmployees, loadReservations, loadTimeoffRequests, loadShiftSwapRequests, loadAnnouncements, loadPosts, loadFeaturedEvents, loadBusinessHours]);

  const handleAddFeaturedEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/featured-event`, newFeaturedEvent, {
        withCredentials: true
      });
      setNewFeaturedEvent({ title: '', subtitle: '', date: '', time: '', description: '', image_url: '' });
      setShowAddFeaturedEvent(false);
      loadFeaturedEvents();
      toast.success('Featured event saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create featured event');
    }
  };

  const activateFeaturedEvent = async (eventId) => {
    try {
      await axios.patch(`${API}/featured-event/${eventId}/activate`, {}, {
        withCredentials: true
      });
      loadFeaturedEvents();
      toast.success('Event activated');
    } catch (err) {
      toast.error('Failed to activate event');
    }
  };

  const deleteFeaturedEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await axios.delete(`${API}/featured-event/${eventId}`, {
        withCredentials: true
      });
      loadFeaturedEvents();
      toast.success('Event deleted');
    } catch (err) {
      toast.error('Failed to delete event');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
      navigate('/team');
    } catch (err) {
      console.error('Logout failed', err);
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/employees`, newEmployee, {
        withCredentials: true
      });
      
      setNewEmployee({ employee_id: '', name: '', password: '', position: '', is_active: true, email: '' });
      setShowAddEmployee(false);
      loadEmployees();
      toast.success('User added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create employee');
    }
  };

  const toggleEmployeeStatus = async (employeeId, currentStatus) => {
    try {
      await axios.put(
        `${API}/admin/employees/${employeeId}`,
        { is_active: !currentStatus },
        { withCredentials: true }
      );
      loadEmployees();
      toast.success(currentStatus ? 'Employee disabled' : 'Employee enabled');
    } catch (err) {
      toast.error('Failed to update employee status');
    }
  };

  const handleTimeoffAction = async (requestId, status) => {
    try {
      await axios.put(
        `${API}/admin/timeoff-requests/${requestId}`,
        { status, reviewed_by: user.employee_id },
        { withCredentials: true }
      );
      loadTimeoffRequests();
      toast.success(`Request ${status}`);
    } catch (err) {
      toast.error('Failed to update request');
    }
  };

  const handleShiftSwapAction = async (requestId, status) => {
    try {
      await axios.put(
        `${API}/admin/shift-swap/${requestId}`,
        { status, reviewed_by: user.employee_id },
        { withCredentials: true }
      );
      loadShiftSwapRequests();
      toast.success(`Shift swap ${status}`);
    } catch (err) {
      toast.error('Failed to update shift swap');
    }
  };

  const handleScheduleUpload = async (e) => {
    e.preventDefault();
    if (!scheduleFile) {
      toast.error('Please select a file');
      return;
    }
    if (!weekStart || !weekEnd) {
      toast.error('Please select both a start and ending date');
      return;
    }
    if (weekEnd < weekStart) {
      toast.error('Ending date cannot be before the starting date');
      return;
    }

    const formData = new FormData();
    formData.append('file', scheduleFile);
    formData.append('week_start', weekStart);
    formData.append('week_end', weekEnd);

    try {
      await axios.post(`${API}/admin/schedule/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Schedule uploaded successfully');
      setScheduleFile(null);
      setWeekStart('');
      setWeekEnd('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload schedule');
    }
  };

  const handleAddAnnouncement = async (e) => {
    e.preventDefault();
    try {
      if (editingAnnouncement) {
        await axios.put(
          `${API}/admin/announcements/${editingAnnouncement.id}`,
          newAnnouncement,
          { withCredentials: true }
        );
      } else {
        await axios.post(`${API}/admin/announcements`, newAnnouncement, {
          withCredentials: true
        });
      }
      
      setNewAnnouncement({ title: '', message: '' });
      setShowAddAnnouncement(false);
      setEditingAnnouncement(null);
      loadAnnouncements();
      toast.success(editingAnnouncement ? 'Announcement updated' : 'Announcement posted');
    } catch (err) {
      toast.error('Failed to save announcement');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    
    try {
      await axios.delete(`${API}/admin/announcements/${id}`, {
        withCredentials: true
      });
      loadAnnouncements();
      toast.success('Announcement deleted');
    } catch (err) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    
    const formData = new FormData();
    if (postContent) formData.append('content', postContent);
    if (postImage) formData.append('image', postImage);

    try {
      await axios.post(`${API}/feed/posts`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPostContent('');
      setPostImage(null);
      loadPosts();
      toast.success('Post published');
    } catch (err) {
      toast.error('Failed to create post');
    }
  };

  const handleToggleLike = async (postId) => {
    try {
      await axios.post(`${API}/feed/posts/${postId}/like`, {}, {
        withCredentials: true
      });
      loadPosts();
    } catch (err) {
      console.error('Failed to toggle like');
    }
  };

  const handleAddComment = async (postId) => {
    const content = commentText[postId];
    if (!content?.trim()) return;

    try {
      await axios.post(`${API}/feed/posts/${postId}/comments`, 
        { content },
        { withCredentials: true }
      );
      
      setCommentText({ ...commentText, [postId]: '' });
      loadComments(postId);
      toast.success('Comment added');
    } catch (err) {
      toast.error('Failed to add comment');
    }
  };

  const loadComments = async (postId) => {
    try {
      const response = await axios.get(`${API}/feed/posts/${postId}/comments`, {
        withCredentials: true
      });
      
      setExpandedComments({
        ...expandedComments,
        [postId]: response.data.comments
      });
    } catch (err) {
      console.error('Failed to load comments');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    
    try {
      await axios.delete(`${API}/feed/posts/${postId}`, {
        withCredentials: true
      });
      loadPosts();
      toast.success('Post deleted');
    } catch (err) {
      toast.error('Failed to delete post');
    }
  };

  const handleDeleteComment = async (commentId, postId) => {
    if (!window.confirm('Delete this comment?')) return;
    
    try {
      await axios.delete(`${API}/feed/comments/${commentId}`, {
        withCredentials: true
      });
      loadComments(postId);
      toast.success('Comment deleted');
    } catch (err) {
      toast.error('Failed to delete comment');
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_elegant-royal-bar/artifacts/6ywx5xvf_CFB2E630-E067-4D50-B2DB-A530D292F3D8.png" 
            alt="Royal Bar & Grill" 
            className="admin-logo"
          />
          <div className="admin-user-info">
            <span>Welcome, {user?.name}</span>
            <button onClick={handleLogout} className="btn-logout">
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="admin-container">
        <div className="admin-tabs">
          <button 
            className={`admin-tab ${activeTab === 'employees' ? 'active' : ''}`}
            onClick={() => setActiveTab('employees')}
            data-testid="employees-tab"
          >
            <Users size={20} />
            Employees
          </button>
          <button 
            className={`admin-tab ${activeTab === 'reservations' ? 'active' : ''}`}
            onClick={() => setActiveTab('reservations')}
            data-testid="reservations-tab"
          >
            <CalendarCheck size={20} />
            Reservations
          </button>
          <button 
            className={`admin-tab ${activeTab === 'hours' ? 'active' : ''}`}
            onClick={() => setActiveTab('hours')}
            data-testid="business-hours-tab"
          >
            <Clock size={20} />
            Business Hours
          </button>
          <button 
            className={`admin-tab ${activeTab === 'timeoff' ? 'active' : ''}`}
            onClick={() => setActiveTab('timeoff')}
            data-testid="timeoff-tab"
          >
            <Clock size={20} />
            Time Off
          </button>
          <button 
            className={`admin-tab ${activeTab === 'shiftswap' ? 'active' : ''}`}
            onClick={() => setActiveTab('shiftswap')}
            data-testid="shiftswap-tab"
          >
            <Calendar size={20} />
            Shift Swaps
          </button>
          <button 
            className={`admin-tab ${activeTab === 'announcements' ? 'active' : ''}`}
            onClick={() => setActiveTab('announcements')}
            data-testid="announcements-tab"
          >
            <Bell size={20} />
            Announcements
          </button>
          <button 
            className={`admin-tab ${activeTab === 'feed' ? 'active' : ''}`}
            onClick={() => setActiveTab('feed')}
            data-testid="feed-tab"
          >
            <MessageSquare size={20} />
            Team Feed
          </button>
          <button 
            className={`admin-tab ${activeTab === 'featured' ? 'active' : ''}`}
            onClick={() => setActiveTab('featured')}
            data-testid="featured-tab"
          >
            <Sparkles size={20} />
            Featured Event
          </button>
          <button 
            className={`admin-tab ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveTab('content')}
            data-testid="content-tab"
          >
            <Settings size={20} />
            Content Manager
          </button>
          <button 
            className={`admin-tab ${activeTab === 'schedule' ? 'active' : ''}`}
            onClick={() => setActiveTab('schedule')}
            data-testid="upload-schedule-tab"
          >
            <Upload size={20} />
            Upload Schedule
          </button>
        </div>

        <div className="admin-content">
          {/* Main Content Area */}
          <div className="admin-main-content">
          {/* EMPLOYEES TAB */}
          {activeTab === 'employees' && (
            <div className="employees-section">
              <div className="section-header">
                <h2>Employee Management</h2>
                <button 
                  className="btn-primary"
                  onClick={() => setShowAddEmployee(true)}
                  data-testid="add-user-btn"
                >
                  <Plus size={20} />
                  Add User
                </button>
              </div>

              {showAddEmployee && (
                <div className="modal-overlay" onClick={() => setShowAddEmployee(false)}>
                  <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>Add User</h3>
                      <button onClick={() => setShowAddEmployee(false)}>
                        <X size={24} />
                      </button>
                    </div>
                    <form onSubmit={handleAddEmployee} className="employee-form" data-testid="add-user-form">
                      <div className="form-group">
                        <label>Employee Name *</label>
                        <input
                          type="text"
                          value={newEmployee.name}
                          onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                          required
                          placeholder="John Doe"
                          data-testid="add-user-name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Role / Position</label>
                        <input
                          type="text"
                          value={newEmployee.position}
                          onChange={(e) => setNewEmployee({...newEmployee, position: e.target.value})}
                          placeholder="e.g., Bartender"
                          data-testid="add-user-position"
                        />
                      </div>
                      <div className="form-group">
                        <label>Login ID / Username *</label>
                        <input
                          type="text"
                          value={newEmployee.employee_id}
                          onChange={(e) => setNewEmployee({...newEmployee, employee_id: e.target.value})}
                          required
                          placeholder="e.g., EMP001"
                          data-testid="add-user-login-id"
                        />
                      </div>
                      <div className="form-group">
                        <label>Temporary Password *</label>
                        <input
                          type="password"
                          value={newEmployee.password}
                          onChange={(e) => setNewEmployee({...newEmployee, password: e.target.value})}
                          required
                          placeholder="Temporary password"
                          data-testid="add-user-password"
                        />
                      </div>
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          value={newEmployee.is_active ? 'active' : 'inactive'}
                          onChange={(e) => setNewEmployee({...newEmployee, is_active: e.target.value === 'active'})}
                          data-testid="add-user-status"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Email (Optional)</label>
                        <input
                          type="email"
                          value={newEmployee.email}
                          onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                          placeholder="employee@email.com"
                          data-testid="add-user-email"
                        />
                      </div>
                      <button type="submit" className="btn-primary" data-testid="add-user-submit">
                        Add User
                      </button>
                    </form>
                  </div>
                </div>
              )}

              <div className="employees-list">
                {employees.map((emp) => (
                  <div key={emp.id} className="employee-card">
                    <div className="employee-info">
                      <h4>{emp.name}</h4>
                      <p>ID: {emp.employee_id}</p>
                      {emp.position && <p>Role: {emp.position}</p>}
                      {emp.email && <p>Email: {emp.email}</p>}
                    </div>
                    <div className="employee-actions">
                      <span className={`status-badge ${emp.is_active ? 'active' : 'inactive'}`}>
                        {emp.is_active ? 'Active' : 'Disabled'}
                      </span>
                      <button
                        onClick={() => toggleEmployeeStatus(emp.employee_id, emp.is_active)}
                        className={emp.is_active ? 'btn-disable' : 'btn-enable'}
                      >
                        {emp.is_active ? <UserX size={18} /> : <Check size={18} />}
                        {emp.is_active ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TIME OFF TAB */}
          {/* RESERVATIONS TAB */}
          {activeTab === 'reservations' && (
            <div className="timeoff-section" data-testid="reservations-section">
              <h2>Reservation Requests</h2>
              <div className="timeoff-list">
                {reservations.length === 0 ? (
                  <p className="no-data">No reservation requests yet</p>
                ) : (
                  reservations.map((r) => (
                    <div key={r.id} className="timeoff-card" data-testid={`reservation-${r.id}`}>
                      <div className="timeoff-info">
                        <h4>{r.name} · {r.party_size} {parseInt(r.party_size, 10) === 1 ? 'guest' : 'guests'}</h4>
                        <p><strong>When:</strong> {r.date} at {r.time}</p>
                        <p><strong>Phone:</strong> {r.phone}</p>
                        <p><strong>Email:</strong> {r.email}</p>
                        {r.notes && <p><strong>Notes:</strong> {r.notes}</p>}
                        <p className="timeoff-date">
                          Submitted: {r.created_at ? new Date(r.created_at).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }) : '—'} (PT)
                        </p>
                      </div>
                      <div className="timeoff-actions">
                        <span className={`status-badge status-${r.status}`}>
                          {r.status}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* BUSINESS HOURS TAB */}
          {activeTab === 'hours' && (
            <div className="business-hours-section" data-testid="business-hours-section">
              <h2>Business Hours</h2>
              <p className="business-hours-subtitle">These hours are the single source of truth and update everywhere on the website.</p>
              {businessHours ? (
                <>
                  <div className="hours-editor">
                    {DAY_ORDER.map((key) => (
                      <div key={`edit-${key}`} className="hours-editor-row" data-testid={`hours-row-${key}`}>
                        <span className="hours-editor-day">{DAY_LABELS[key]}</span>
                        <div className="hours-editor-times">
                          <input
                            type="time"
                            value={businessHours[key].open}
                            disabled={businessHours[key].closed}
                            onChange={(e) => updateDayHours(key, 'open', e.target.value)}
                            data-testid={`open-${key}`}
                          />
                          <span className="hours-to">to</span>
                          <input
                            type="time"
                            value={businessHours[key].close}
                            disabled={businessHours[key].closed}
                            onChange={(e) => updateDayHours(key, 'close', e.target.value)}
                            data-testid={`close-${key}`}
                          />
                        </div>
                        <label className="closed-toggle">
                          <input
                            type="checkbox"
                            checked={businessHours[key].closed}
                            onChange={(e) => updateDayHours(key, 'closed', e.target.checked)}
                            data-testid={`closed-${key}`}
                          />
                          Closed
                        </label>
                      </div>
                    ))}
                  </div>
                  <button
                    className="btn-primary"
                    onClick={handleSaveHours}
                    disabled={savingHours}
                    data-testid="save-hours-btn"
                  >
                    {savingHours ? 'Saving...' : 'Save Hours'}
                  </button>
                </>
              ) : (
                <p className="no-data">Loading business hours...</p>
              )}
            </div>
          )}


          {/* TIME OFF TAB */}
          {activeTab === 'timeoff' && (
            <div className="timeoff-section">
              <h2>Time Off Requests</h2>
              <div className="timeoff-list">
                {timeoffRequests.length === 0 ? (
                  <p className="no-data">No time-off requests</p>
                ) : (
                  timeoffRequests.map((request) => (
                    <div key={request.id} className="timeoff-card">
                      <div className="timeoff-info">
                        <h4>{request.employee_name}</h4>
                        <p>ID: {request.employee_id}</p>
                        <p><strong>Dates:</strong> {request.start_date} to {request.end_date}</p>
                        {request.reason && <p><strong>Reason:</strong> {request.reason}</p>}
                        <p className="timeoff-date">Requested: {new Date(request.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="timeoff-actions">
                        <span className={`status-badge status-${request.status}`}>
                          {request.status}
                        </span>
                        {request.status === 'pending' && (
                          <div className="action-buttons">
                            <button
                              onClick={() => handleTimeoffAction(request.id, 'approved')}
                              className="btn-approve"
                            >
                              <Check size={18} />
                              Approve
                            </button>
                            <button
                              onClick={() => handleTimeoffAction(request.id, 'denied')}
                              className="btn-deny"
                            >
                              <X size={18} />
                              Deny
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* SHIFT SWAP TAB */}
          {activeTab === 'shiftswap' && (
            <div className="timeoff-section">
              <h2>Shift Swap Requests</h2>
              <div className="timeoff-list">
                {shiftSwapRequests.length === 0 ? (
                  <p className="no-data">No shift swap requests</p>
                ) : (
                  shiftSwapRequests.map((request) => (
                    <div key={request.id} className="timeoff-card">
                      <div className="timeoff-info">
                        <h4>{request.employee_name}</h4>
                        <p>ID: {request.employee_id}</p>
                        <p><strong>Date:</strong> {request.shift_date}</p>
                        <p><strong>Time:</strong> {request.shift_time}</p>
                        {request.swap_with && <p><strong>Swap with:</strong> {request.swap_with}</p>}
                        {request.notes && <p><strong>Notes:</strong> {request.notes}</p>}
                        <p className="timeoff-date">Requested: {new Date(request.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="timeoff-actions">
                        <span className={`status-badge status-${request.status}`}>
                          {request.status}
                        </span>
                        {request.status === 'pending' && (
                          <div className="action-buttons">
                            <button
                              onClick={() => handleShiftSwapAction(request.id, 'approved')}
                              className="btn-approve"
                            >
                              <Check size={18} />
                              Approve
                            </button>
                            <button
                              onClick={() => handleShiftSwapAction(request.id, 'denied')}
                              className="btn-deny"
                            >
                              <X size={18} />
                              Deny
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ANNOUNCEMENTS TAB */}
          {activeTab === 'announcements' && (
            <div className="announcements-section">
              <div className="section-header">
                <h2>Announcements</h2>
                <button 
                  className="btn-primary"
                  onClick={() => {
                    setShowAddAnnouncement(true);
                    setEditingAnnouncement(null);
                    setNewAnnouncement({ title: '', message: '' });
                  }}
                >
                  <Plus size={20} />
                  New Announcement
                </button>
              </div>

              {showAddAnnouncement && (
                <div className="modal-overlay" onClick={() => setShowAddAnnouncement(false)}>
                  <div className="modal-content announcement-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>{editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}</h3>
                      <button onClick={() => setShowAddAnnouncement(false)}>
                        <X size={24} />
                      </button>
                    </div>
                    <form onSubmit={handleAddAnnouncement} className="announcement-form">
                      <div className="form-group">
                        <label>Title *</label>
                        <input
                          type="text"
                          value={newAnnouncement.title}
                          onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
                          required
                          placeholder="Important Update"
                        />
                      </div>
                      <div className="form-group">
                        <label>Message *</label>
                        <textarea
                          value={newAnnouncement.message}
                          onChange={(e) => setNewAnnouncement({...newAnnouncement, message: e.target.value})}
                          required
                          rows="5"
                          placeholder="Enter announcement details..."
                        />
                      </div>
                      <button type="submit" className="btn-primary">
                        {editingAnnouncement ? 'Update' : 'Post'} Announcement
                      </button>
                    </form>
                  </div>
                </div>
              )}

              <div className="announcements-list">
                {announcements.length === 0 ? (
                  <p className="no-data">No announcements yet</p>
                ) : (
                  announcements.map((announcement) => (
                    <div key={announcement.id} className="announcement-card">
                      <div className="announcement-header">
                        <div>
                          <h3>{announcement.title}</h3>
                          <p className="announcement-meta">
                            By {announcement.created_by_name} • {new Date(announcement.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="announcement-actions">
                          <button 
                            onClick={() => {
                              setEditingAnnouncement(announcement);
                              setNewAnnouncement({ title: announcement.title, message: announcement.message });
                              setShowAddAnnouncement(true);
                            }}
                            className="btn-icon"
                          >
                            <Eye size={18} />
                          </button>
                          <button 
                            onClick={() => handleDeleteAnnouncement(announcement.id)}
                            className="btn-icon btn-danger"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                      <p className="announcement-message">{announcement.message}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* TEAM FEED TAB */}
          {activeTab === 'feed' && (
            <div className="feed-section">
              <h2>Team Feed</h2>
              
              <div className="create-post-card">
                <form onSubmit={handleCreatePost} className="create-post-form">
                  <textarea
                    value={postContent}
                    onChange={(e) => setPostContent(e.target.value)}
                    placeholder="Share an update with the team..."
                    maxLength="500"
                    rows="3"
                  />
                  <div className="post-actions">
                    <label className="image-upload-btn">
                      <ImageIcon size={20} />
                      {postImage ? postImage.name : 'Add Photo'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setPostImage(e.target.files[0])}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <button type="submit" className="btn-primary" disabled={!postContent && !postImage}>
                      Post Update
                    </button>
                  </div>
                  {postImage && (
                    <div className="image-preview">
                      <span>{postImage.name}</span>
                      <button type="button" onClick={() => setPostImage(null)}>
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </form>
              </div>

              <div className="posts-list">
                {posts.map((post) => (
                  <div key={post.id} className="post-card">
                    <div className="post-header">
                      <div>
                        <h4>{post.author_name}</h4>
                        <span className="post-time">{new Date(post.created_at).toLocaleString()}</span>
                      </div>
                      <button 
                        onClick={() => handleDeletePost(post.id)}
                        className="btn-icon btn-danger"
                        title="Delete post"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                    
                    {post.content && <p className="post-content">{post.content}</p>}
                    
                    {post.image_url && (
                      <img 
                        src={`${BACKEND_URL}${post.image_url}`} 
                        alt="Post" 
                        className="post-image"
                      />
                    )}
                    
                    <div className="post-stats">
                      <button 
                        onClick={() => handleToggleLike(post.id)}
                        className={`btn-like ${post.user_liked ? 'liked' : ''}`}
                      >
                        <Heart size={18} fill={post.user_liked ? 'currentColor' : 'none'} />
                        {post.like_count}
                      </button>
                      <button 
                        onClick={() => {
                          if (expandedComments[post.id]) {
                            setExpandedComments({ ...expandedComments, [post.id]: null });
                          } else {
                            loadComments(post.id);
                          }
                        }}
                        className="btn-comment"
                      >
                        <MessageSquare size={18} />
                        {post.comment_count}
                      </button>
                    </div>

                    {expandedComments[post.id] && (
                      <div className="comments-section">
                        <div className="add-comment">
                          <input
                            type="text"
                            placeholder="Add a comment..."
                            value={commentText[post.id] || ''}
                            onChange={(e) => setCommentText({ ...commentText, [post.id]: e.target.value })}
                            maxLength="250"
                          />
                          <button onClick={() => handleAddComment(post.id)}>Post</button>
                        </div>
                        <div className="comments-list">
                          {expandedComments[post.id]?.map((comment) => (
                            <div key={comment.id} className="comment">
                              <div className="comment-header">
                                <strong>{comment.author_name}</strong>
                                <button 
                                  onClick={() => handleDeleteComment(comment.id, post.id)}
                                  className="btn-icon-small"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                              <p>{comment.content}</p>
                              <span className="comment-time">{new Date(comment.created_at).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SCHEDULE TAB */}
          {activeTab === 'schedule' && (
            <div className="schedule-section">
              <h2>Upload Weekly Schedule</h2>
              <form onSubmit={handleScheduleUpload} className="schedule-form">
                <div className="form-group">
                  <label>Week Starting Date</label>
                  <input
                    type="date"
                    value={weekStart}
                    onChange={(e) => setWeekStart(e.target.value)}
                    className="date-input"
                    data-testid="schedule-start-date"
                  />
                </div>
                <div className="form-group">
                  <label>Schedule Ending Date</label>
                  <input
                    type="date"
                    value={weekEnd}
                    min={weekStart || undefined}
                    onChange={(e) => setWeekEnd(e.target.value)}
                    className="date-input"
                    data-testid="schedule-end-date"
                  />
                  <p className="form-hint">The schedule stays active from the start date through the ending date (Pacific Time). After the ending date it is no longer shown as current.</p>
                </div>
                <div className="form-group">
                  <label>Schedule File (PDF or Image)</label>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) => setScheduleFile(e.target.files[0])}
                    className="file-input"
                    data-testid="schedule-file-input"
                  />
                  {scheduleFile && (
                    <p className="file-selected">
                      <FileText size={16} />
                      {scheduleFile.name}
                    </p>
                  )}
                </div>
                <button type="submit" className="btn-primary" data-testid="upload-schedule-submit">
                  <Upload size={20} />
                  Upload Schedule
                </button>
              </form>
            </div>
          )}

          {/* CONTENT MANAGER TAB */}
          {activeTab === 'content' && (
            <ContentManager />
          )}

          {/* FEATURED EVENT TAB */}
          {activeTab === 'featured' && (
            <div className="featured-section" data-testid="featured-section">
              <div className="section-header">
                <h2>Homepage Featured Event</h2>
                <button 
                  className="btn-primary"
                  onClick={() => setShowAddFeaturedEvent(true)}
                >
                  <Plus size={20} />
                  Set New Event
                </button>
              </div>
              
              <p className="section-description">
                The featured event will appear on the homepage hero section, visible to all visitors.
              </p>

              {/* Add/Edit Featured Event Form */}
              {showAddFeaturedEvent && (
                <div className="modal-overlay" onClick={() => setShowAddFeaturedEvent(false)}>
                  <div className="modal-content" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>Set Featured Event</h3>
                      <button onClick={() => setShowAddFeaturedEvent(false)} className="btn-close">
                        <X size={20} />
                      </button>
                    </div>
                    <form onSubmit={handleAddFeaturedEvent}>
                      <div className="form-group">
                        <label>Event Title *</label>
                        <input
                          type="text"
                          placeholder="e.g., Super Bowl Sunday"
                          value={newFeaturedEvent.title}
                          onChange={(e) => setNewFeaturedEvent({...newFeaturedEvent, title: e.target.value})}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Subtitle (optional)</label>
                        <input
                          type="text"
                          placeholder="e.g., Big Screen • Drink Specials • Wings"
                          value={newFeaturedEvent.subtitle}
                          onChange={(e) => setNewFeaturedEvent({...newFeaturedEvent, subtitle: e.target.value})}
                        />
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Date *</label>
                          <input
                            type="text"
                            placeholder="e.g., February 9th, 2025"
                            value={newFeaturedEvent.date}
                            onChange={(e) => setNewFeaturedEvent({...newFeaturedEvent, date: e.target.value})}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Time (optional)</label>
                          <input
                            type="text"
                            placeholder="e.g., 3 PM - Close"
                            value={newFeaturedEvent.time}
                            onChange={(e) => setNewFeaturedEvent({...newFeaturedEvent, time: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Description (optional)</label>
                        <textarea
                          placeholder="Additional details about the event..."
                          value={newFeaturedEvent.description}
                          onChange={(e) => setNewFeaturedEvent({...newFeaturedEvent, description: e.target.value})}
                          rows={3}
                        />
                      </div>
                      <button type="submit" className="btn-primary">
                        <Star size={20} />
                        Set as Featured Event
                      </button>
                    </form>
                  </div>
                </div>
              )}

              {/* Featured Events List */}
              <div className="featured-events-list">
                {featuredEvents.length === 0 ? (
                  <div className="empty-state">
                    <Sparkles size={48} />
                    <p>No featured events yet. Create one to display on your homepage!</p>
                  </div>
                ) : (
                  featuredEvents.map(event => (
                    <div 
                      key={event.id} 
                      className={`featured-event-card ${event.is_active ? 'active' : ''}`}
                      data-testid={`featured-event-${event.id}`}
                    >
                      {event.is_active && (
                        <div className="active-badge">
                          <Star size={14} />
                          Currently on Homepage
                        </div>
                      )}
                      <div className="event-info">
                        <h3>{event.title}</h3>
                        {event.subtitle && <p className="event-subtitle">{event.subtitle}</p>}
                        <div className="event-meta">
                          <span><Calendar size={14} /> {event.date}</span>
                          {event.time && <span><Clock size={14} /> {event.time}</span>}
                        </div>
                        {event.description && <p className="event-description">{event.description}</p>}
                      </div>
                      <div className="event-actions">
                        {!event.is_active && (
                          <button 
                            className="btn-activate"
                            onClick={() => activateFeaturedEvent(event.id)}
                          >
                            <Star size={16} />
                            Set as Featured
                          </button>
                        )}
                        <button 
                          className="btn-delete"
                          onClick={() => deleteFeaturedEvent(event.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          </div>

          {/* Online Users Sidebar - Always visible */}
          <div className="online-users-sidebar">
            <OnlineUsers />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
